import {
  ref,
  uploadBytes,
  getDownloadURL,
  getMetadata,
} from 'firebase/storage';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../../firebase';
import { DOCUMENT_TYPES, getDocumentUrl } from './DocumentTypeConfig';
import { notifyDocumentSaved } from '../../../utils/documentsHostBridge';
import { newUuid } from '../../../utils/newUuid';

/**
 * Upload document artifacts (PDF and/or composition image) to Firebase Storage.
 * Returns { url, compositionUrl, storagePath }
 */
export async function uploadDocumentArtifacts(
  projectId,
  docTypeKey,
  pdfBlob,
  compositionBlob = null,
  filename = null
) {
  const config = DOCUMENT_TYPES[docTypeKey];
  if (!config) throw new Error(`Unknown document type: ${docTypeKey}`);

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const baseName = filename || `${config.firestoreKey}_${timestamp}`;
  const storagePath = `projects/${projectId}/documents/${config.firestoreKey}/${baseName}`;

  // Upload PDF
  const pdfPath = `${storagePath}.pdf`;
  const pdfRef = ref(storage, pdfPath);
  await uploadBytes(pdfRef, pdfBlob);
  const url = await getDownloadURL(pdfRef);

  // Upload composition image if provided
  let compositionUrl = null;
  if (compositionBlob) {
    const ext = compositionBlob.type === 'image/jpeg' ? 'jpg' : 'png';
    const compPath = `${storagePath}_composition.${ext}`;
    const compRef = ref(storage, compPath);
    await uploadBytes(compRef, compositionBlob);
    compositionUrl = await getDownloadURL(compRef);
  }

  return { url, compositionUrl, storagePath, sizeBytes: pdfBlob.size };
}

/**
 * Upload a single image (e.g., cover rendering JPEG) to Firebase Storage.
 * Returns { url, storagePath }
 */
export async function uploadDocumentImage(
  projectId,
  docTypeKey,
  imageBlob,
  filename = null
) {
  const config = DOCUMENT_TYPES[docTypeKey];
  if (!config) throw new Error(`Unknown document type: ${docTypeKey}`);

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const ext = imageBlob.type === 'image/jpeg' ? 'jpg' : 'png';
  const baseName = filename || `${config.firestoreKey}_${timestamp}`;
  const storagePath = `projects/${projectId}/documents/${config.firestoreKey}/${baseName}.${ext}`;

  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, imageBlob);
  const url = await getDownloadURL(storageRef);

  return { url, storagePath, sizeBytes: imageBlob.size };
}

/**
 * Size in bytes of an already-uploaded document, for entries saved before
 * `sizeBytes` was recorded. Resolves the download URL to its storage object.
 * Returns null when the object can't be read.
 */
export async function fetchDocumentSizeBytes(url) {
  if (!url) return null;
  try {
    const meta = await getMetadata(ref(storage, url));
    return typeof meta.size === 'number' ? meta.size : null;
  } catch (e) {
    return null;
  }
}

/**
 * Append a document metadata entry to the appropriate Firestore array.
 */
export async function saveDocumentMetadata(projectId, docTypeKey, metadata) {
  const config = DOCUMENT_TYPES[docTypeKey];
  if (!config) throw new Error(`Unknown document type: ${docTypeKey}`);

  const projectRef = doc(db, 'projects', projectId);
  const projectSnap = await getDoc(projectRef);
  if (!projectSnap.exists()) throw new Error('Project not found');

  const projectData = projectSnap.data();
  const existingDocs = projectData[config.firestoreKey] || [];

  const newDoc = {
    // Stable identity, matching what the iOS writers store. The iOS CD
    // wizard keys room-package selection on it.
    id: newUuid(),
    ...metadata,
    createdAt: new Date().toISOString(),
    type: config.firestoreKey.replace('Docs', '').replace('Doc', ''),
  };

  await updateDoc(projectRef, {
    [config.firestoreKey]: [...existingDocs, newDoc],
  });
  notifyDocumentSaved({
    docType: docTypeKey,
    firestoreKey: config.firestoreKey,
    url: getDocumentUrl(newDoc),
    name: newDoc.name,
  });

  return newDoc;
}

/**
 * Delete a document entry by its createdAt timestamp.
 */
export async function deleteDocumentByCreatedAt(
  projectId,
  docTypeKey,
  createdAt
) {
  const config = DOCUMENT_TYPES[docTypeKey];
  if (!config) throw new Error(`Unknown document type: ${docTypeKey}`);

  const projectRef = doc(db, 'projects', projectId);
  const projectSnap = await getDoc(projectRef);
  if (!projectSnap.exists()) throw new Error('Project not found');

  const projectData = projectSnap.data();
  const existingDocs = projectData[config.firestoreKey] || [];
  const filtered = existingDocs.filter((d) => d.createdAt !== createdAt);

  await updateDoc(projectRef, {
    [config.firestoreKey]: filtered,
  });
}
